import React, { useEffect, useState } from 'react';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { SvgIcon } from '@progress/kendo-react-common';
import * as svg from '@progress/kendo-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { UserApi } from '../../../../../../redux/api/UserApi';
import AddAction from '../../AddAction';
import { TicketApi } from '../../../../../../redux/api/TicketApi';
import { Field, Form, FormElement } from '@progress/kendo-react-form';
import Type from '../../Type';
import { useReduxDispatch, useReduxSelector } from '../../../../../../redux/hooks';
import validateCustomFields from '../../../../../CustomForms/Validators/CustomFieldValidator';
import { OwnershipOptions } from './Ownership';
import CustomFormControl from '../../../../../CustomForms/CustomForm';
import { PartialSpinny } from '../../../../../Nav/Spinny';
import { ISetStatusProps } from '../../../../../../types/Ticket/Status/ISetStatusProps';
import { Card, CardBody } from '@progress/kendo-react-layout';
import { setField, setForm } from '../../../../../../slices/TicketDetailSlice';
import { IActionDTO } from '../../../../../../types/Action/IAction';
import { useGetHostConfigQuery } from '../../../../../../redux/api/AccountApi';
import useRedirectOnClose from '../../../../../../hooks/useRedirectOnClose';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';

const ValidationPopup: React.FC = () => {
    const actionState = useReduxSelector(state => state.actionDetails.action);
    const navigate = useNavigate();
    const { ticketId, statusId } = useParams();
    const { data: allStatuses } = TicketApi.useGetStatusesQuery();
    const { data: userProfile } = UserApi.useGetUserProfileQuery();
    const { data: ticket } = TicketApi.useGetTicketDetailsQuery({ ticketId: Number(ticketId) });
    const { data: ticketForm } = TicketApi.useGetTicketFormQuery(ticket?.ID, { skip: !(ticket?.CustomFormId > 0) });
    const [setStatus, { isLoading: updatingStatus, error: statusUpdateError }] = TicketApi.useSetStatusMutation();
    const [setAssigned, { isLoading: settingOwnership }] = TicketApi.useReassignMutation();
    const [formIndex, setFormIndex] = React.useState<number>(0);
    //const [valMessages, setValMessages] = React.useState<string[]>([]);
    const [valCodes, setValCodes] = React.useState<number[]>(null);
    const [ownership, setOwnership] = React.useState<OwnershipOptions>({ option: 'me' } as OwnershipOptions);
    const closedStatus = allStatuses?.find(s => s.ID === parseInt(statusId)) || allStatuses?.find(s => s.Closed_default === true);
    const { form: stateFields } = useReduxSelector(state => state.ticketDetails);
    const dispatch = useReduxDispatch();
    const [updateFormFields, { isLoading: isUpdatingForm }] = TicketApi.useUpdateTicketFormMutation();
    const [postAction, { isLoading: savingAction }] = TicketApi.useCreateActionMutation();
    const handleRedirect = useRedirectOnClose();
    const [showTypeList, setShowTypeList] = React.useState<boolean>(false);
    const [autoCloseDate, setAutoCloseDate] = useState(null);

    //Put the form fields into the state slice
    useEffect(() => {
        if (ticketForm) {
            dispatch(setForm(ticketForm));
        }
    }, [ticketForm]);

    const fieldChanged = (e) => {
        console.log('fieldChanged', e)
        //update the Ticket details Slice with the new field value
        dispatch(setField(e.value))
    };

    useEffect(() => {
        if (!valCodes && ticket?.CloseValidationCodes)
            setValCodes(ticket?.CloseValidationCodes);

        //Check if the save and close functionlity is being used and the -1 code is not in the list
        if (valCodes?.includes(-1) && !ticket?.CloseValidationCodes?.includes(-1)) {
            setValCodes(codes => [...codes.filter(f => f !== -1)]);
        }

        //Remove the validation to own the ticket before closing
        if (valCodes?.includes(-7) && !ticket?.CloseValidationCodes?.includes(-7)) {
            setValCodes(codes => [...codes.filter(f => f !== -7)]);
        }

        //Auto closed status needs to prompt for date
        if (closedStatus?.Open_status === 4 && closedStatus?.Auto_Status_Interval === 4 && !valCodes?.includes(-8)) {
            setValCodes(codes => [...codes, -8]);
        }

        console.warn('valCodes', valCodes, ticket?.CloseValidationCodes)
    }, [ticket?.CloseValidationCodes]);

    //The trhe possible form validation steps
    const formValidations = [
        {
            name: 'Auto-Close Date',
            header: <><SvgIcon size='large' icon={svg.calendarDateIcon} className='mr-2' ></SvgIcon>Auto-Close</>,
            valCode: -8,
            element: <div className='w-50 m-4'><p>Select the date when this {userProfile?.Phrases.CallPhrase} will automatically close:</p>
                <DateTimePicker placeholder='Select date/time' min={new Date()} onChange={(e) => setAutoCloseDate(e.value.toISOString())} /></div>,
            nextText: null,
            validate: () => {
                return true;
            }
        },
        {
            name: 'Action',
            header: <><SvgIcon size='large' icon={svg.textareaIcon} className='mr-2' ></SvgIcon>Notes</>,
            valCode: -1,
            element: <AddAction showSaveButton={false} ticket={ticket} />,
            validate: () => {

                return true;
            }
        },
        {
            name: 'Type',
            header: <><SvgIcon size='large' icon={svg.categorizeIcon} className='mr-2' ></SvgIcon>{userProfile?.Phrases.Type}</>,
            message: '<Phrase>CallPhrase</Phrase> {0} requires a <phrase>CallPhrase</phrase> type and all sub types to be set before it can be closed',
            valCode: -2,
            element: <div className='w-50 m-4'>
                <label>Type:</label>
                <Type {...ticket} Is_Loading={false} showHeader={false} isOpen={showTypeList} /></div>,
            validate: () => {
                return true;
            }
        },
        {
            name: 'Forms',
            header: <><SvgIcon size='large' icon={svg.formElementIcon} className='mr-2' ></SvgIcon>{ticketForm?.Name}</>,
            valCode: -4,
            //element: <CustomForm {...ticket} showSaveButton={false} />,
            element: <Field id={"customform"}
                name={"customform"}
                component={CustomFormControl}
                form={ticketForm}
                fields={stateFields?.Fields}
                companyId={ticket?.Company_Id}
                onChange={fieldChanged} />,

            validate: () => {


                return true;
            }
        }
    ];

    const displayForms = formValidations.filter(f => valCodes?.includes(f.valCode));
    const isFormStep = displayForms[formIndex]?.valCode === -4;
    const isActionStep = displayForms[formIndex]?.valCode === -1;
    const isAutoClose = displayForms[formIndex]?.valCode === -8;
    const typeStep = displayForms[formIndex]?.valCode === -2 && ticket.CloseValidationCodes?.includes(-2);
    useEffect(() => {

        setShowTypeList(typeStep);

    }, [typeStep]);

    const validators = (values) => {

        let customFieldValidators = { ...validateCustomFields(stateFields?.Fields, true) }

        let val = {
            //...(!ticket.Call_Desc?.trim()?.length && { "description": `${userProfile?.Phrases.Description} is required` }),
            //...(!ticket.Project_Id && !bulkLog && { "project": ` (required)` }),
            ...(isAutoClose && !autoCloseDate && { "autoClose": 'Auto-Close date is required' }),
            ...(typeStep && { "type": `${userProfile.Phrases.CallPhrase} requires a ${userProfile.Phrases.Type} and all sub types to be set before it can be closed` }),
            ...(isActionStep && !actionState?.HTML_Notes && { "action": `Action note is required` }),
            ...(isFormStep && Object.keys(customFieldValidators).length && { "form": `${Object.keys(customFieldValidators).length} fields required` }),
            //...formValidator
            ...(isFormStep && customFieldValidators)
        }

        //console.log('formValidations[formIndex]?.valCode', displayForms[formIndex]?.valCode, formIndex, displayForms)

        return val;
    }

    const header = `This ${userProfile.Phrases.CallPhrase} has ${displayForms.length} closing requirement${displayForms.length > 1 ? 's' : ''}: ${displayForms.map(d => d.name).join(', ')}`;
    const formSubmit = async (values, event) => {
        //console.log('formSubmit', values, event)

        //If this is the last step then close the ticket
        if (formIndex >= displayForms.length - 1) {
            //console.log('close ticket', ticketId, statusId, closedStatus, ownership)

            //Save Ownership
            // if (ownership && displayForms.find(f => f.valCode === -7)) {
            //     var reassignProps = { TicketId: ticket.ID, New_Assignees: [userProfile.Id] } as ReassignProps
            //     if (ownership.option === 'someone' && ownership.assignedToId)
            //         reassignProps.New_Assignees = [ownership.assignedToId];

            //     await setAssigned(reassignProps).unwrap();
            // }

            //Save action but only if it STILL requires an action. The action may already have been saved but the status update failed and the user is tryiung again
            if (actionState?.HTML_Notes && ticket?.CloseValidationCodes.find(f => f === -1)) {
                let actionTypeId = actionState.Action_Type_Id ?? (userProfile.Features.External_Interface ? 8 : 1);
                let saveAction = { ...actionState, Call_Id: ticket.ID, Notes: actionState.HTML_Notes, Action_Type_Id: actionTypeId, Contact_Id: actionState.Staff_Id } as IActionDTO
                await postAction(saveAction).unwrap();
            }


            //Save custom forms
            if (displayForms.find(f => f.valCode === -4)) {
                await updateFormFields({ ticketId: ticket.ID, formId: ticket.CustomFormId, fields: stateFields?.Fields }).unwrap();
            }

            //SEND it bru!
            const props = { New_Status_Id: closedStatus?.ID, Ticket_Id: parseInt(ticketId) } as ISetStatusProps
            if (displayForms.find(f => f.valCode === -8) && autoCloseDate) {
                //Set the auto close date
                props.Auto_Close_Date = autoCloseDate;
            }

            await setStatus(props).unwrap().then((hmm) => {
                //May need to navigate back to the homnepage if that options is enabled
                handleRedirect(props.Ticket_Id);
            }).catch((err) => {
                if (err.originalStatus === 400) {
                    //navigate(`close/${statusId}`);
                    console.log('Validation error', err)
                }
            });

            return;
        }

        //validate the curtrent field
        var isValid = displayForms[formIndex]?.validate && displayForms[formIndex]?.validate();
        if (!isValid)
            return;

        setFormIndex(i => i + 1)
    }

    return (
        <Form validator={validators} ignoreModified={true} id="close-ticket-form" onSubmit={formSubmit} render={(formRenderProps) =>
        (<FormElement >
            <Window resizable={false} modal={true} className='cool-window fixed-window close-validation-popup' initialHeight={600} initialWidth={Math.min(1000, window.innerWidth - 10)} maximizeButton={() => null}
                minimizeButton={() => null} title={
                    <div><SvgIcon size='large' icon={svg.exclamationCircleIcon} className='mr-2' ></SvgIcon>{header}</div>
                } onClose={() => navigate(`/ticket/${ticketId}`)}>
                {(settingOwnership || updatingStatus || isUpdatingForm || savingAction) && <PartialSpinny />}
                <Card>
                    <CardBody>
                        {/* formRenderProps = {JSON.stringify(formRenderProps)} */}
                        {displayForms[formIndex]?.header &&
                            <div className='header'><h6>{displayForms[formIndex]?.header}</h6></div>}
                        {(statusUpdateError || (Object.keys(formRenderProps?.errors).filter(k => k.indexOf('field_') !== 0).length > 0 && !formRenderProps.allowSubmit)) && <div className='k-messagebox k-messagebox-error p-3'>
                            {Object.keys(formRenderProps.errors).filter(k => k.indexOf('field_') !== 0).map((m, i) => <div key={i}>{formRenderProps.errors[m]}</div>)}
                            {statusUpdateError && ('data' in statusUpdateError) && <div>{JSON.stringify(statusUpdateError)}</div>}
                        </div>}
                        {/* {displayForms && displayForms[formIndex]?.element} */}
                        {Object.keys(displayForms).map((k, i) => {
                            return (<div className={i === formIndex ? '' : 'hidden'}>{displayForms[k]?.element}</div>)
                        })}
                    </CardBody>
                </Card>
                <WindowActionsBar layout='stretched' >
                    <div className="d-flex justify-content-between">
                        <div><Button fillMode='outline' size='large' onClick={(e) => navigate(`/ticket/${ticketId}`)}>Cancel</Button>
                        </div>
                        <div>
                            <small className='text-muted mr-3'>Step {formIndex + 1} of {displayForms.length}</small>
                            {formIndex > 0 && (
                                <Button type='button' style={{ paddingLeft: '15px', paddingRight: '15px' }} size='large' onClick={(e) => {
                                    setFormIndex((i) => i > 0 ? (i - 1) : 0);
                                    //console.log('back', formIndex)
                                }}>&lt;Back </Button>)
                            }<Button disabled={!formRenderProps.allowSubmit} type='submit' form='close-ticket-form' style={{ color: '#fff', backgroundColor: userProfile?.Nav_Hex_Color, paddingLeft: '15px', paddingRight: '15px', marginLeft: 10 }} size='large' >{(formIndex >= displayForms.length - 1 ? `Close ${userProfile?.Phrases.CallPhrase}` : <>Next &gt;</>)}</Button>
                        </div>
                    </div>
                </WindowActionsBar>
            </Window></FormElement>)
        } />
    );
};

export default ValidationPopup;